import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiTrendingDown, FiMinus, FiRefreshCw, FiSave, FiUsers, FiCheck, FiUser, FiLogIn, FiUserPlus } = FiIcons;

const GuestResults = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [shareCode, setShareCode] = useState('');

  useEffect(() => {
    const storedResults = localStorage.getItem('assessmentResults');
    if (storedResults) {
      const data = JSON.parse(storedResults);
      if (!data.isGuestMode) {
        // This is not a guest assessment, redirect to regular results
        navigate('/results');
        return;
      }
      setAssessmentData(data);
      calculateResults(data);
      generateShareCode();
    } else {
      navigate('/assessment');
    }
  }, [navigate]);

  const calculateResults = (responses) => {
    const sections = [
      'Psychological Safety',
      'Dependability',
      'Structure & Clarity',
      'Meaning',
      'Impact'
    ];
    const sectionQuestionCounts = [7, 5, 5, 5, 5];
    const reverseScoreQuestions = ['0-0', '0-2', '0-4', '1-1']; // Questions that need reverse scoring

    const sectionScores = sections.map((section, sectionIndex) => {
      let totalScore = 0;
      let questionCount = sectionQuestionCounts[sectionIndex];

      for (let q = 0; q < questionCount; q++) {
        const key = `${sectionIndex}-${q}`;
        let score = responses.responses[key] || 3;

        // Reverse score certain questions
        if (reverseScoreQuestions.includes(key)) {
          score = 6 - score;
        }

        totalScore += score;
      }

      const average = totalScore / questionCount;
      const percentage = ((average - 1) / 4) * 100;

      return {
        name: section,
        score: average,
        percentage: Math.round(percentage),
        level: getScoreLevel(average)
      };
    });

    const overallScore = sectionScores.reduce((sum, section) => sum + section.score, 0) / sections.length;
    const overallPercentage = Math.round(((overallScore - 1) / 4) * 100);

    setResults({
      sections: sectionScores,
      overall: {
        score: overallScore,
        percentage: overallPercentage,
        level: getScoreLevel(overallScore)
      }
    });
  };

  const generateShareCode = () => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    setShareCode(code);
  };

  const getScoreLevel = (score) => {
    if (score >= 4.5) return { level: 'Excellent', color: 'green', icon: FiTrendingUp };
    if (score >= 3.5) return { level: 'Good', color: 'blue', icon: FiTrendingUp };
    if (score >= 2.5) return { level: 'Fair', color: 'yellow', icon: FiMinus };
    return { level: 'Needs Improvement', color: 'red', icon: FiTrendingDown };
  };

  const getRecommendations = (sectionName, score) => {
    const recommendations = {
      'Psychological Safety': {
        low: [
          'Establish team norms that encourage open dialogue',
          'Practice active listening during meetings',
          'Share failures as learning opportunities',
          'Encourage questions and curiosity'
        ],
        high: [
          'Continue fostering open communication',
          'Share your practices with other teams',
          'Mentor new team members on psychological safety'
        ]
      },
      'Dependability': {
        low: [
          'Implement clear task tracking systems',
          'Set realistic deadlines and expectations',
          'Establish regular check-ins and status updates',
          'Create accountability partnerships'
        ],
        high: [
          'Maintain your strong commitment culture',
          'Document your processes for other teams',
          'Continue celebrating reliability'
        ]
      },
      'Structure & Clarity': {
        low: [
          'Define clear roles and responsibilities',
          'Document team processes and procedures',
          'Establish regular goal-setting sessions',
          'Create visual project dashboards'
        ],
        high: [
          'Keep documentation up to date',
          'Share your clarity practices',
          'Continue regular goal alignment'
        ]
      },
      'Meaning': {
        low: [
          'Connect daily tasks to larger purpose',
          'Share impact stories and user feedback',
          'Discuss personal career goals',
          'Celebrate meaningful achievements'
        ],
        high: [
          'Continue connecting work to purpose',
          'Help others find meaning in their work',
          'Share success stories regularly'
        ]
      },
      'Impact': {
        low: [
          'Make team contributions more visible',
          'Track and share success metrics',
          'Connect with end users/customers',
          'Celebrate team achievements publicly'
        ],
        high: [
          'Continue showcasing your impact',
          'Mentor other teams on visibility',
          'Keep measuring and sharing results'
        ]
      }
    };

    return recommendations[sectionName]?.[score >= 3.5 ? 'high' : 'low'] || [];
  };

  const handleRetakeAssessment = () => {
    // Clear guest assessment data
    localStorage.removeItem('guestAssessment');
    localStorage.removeItem('assessmentResults');
    navigate('/assessment');
  };

  const handleSaveForLater = () => {
    // Save assessment data with timestamp for potential recovery
    const savedAssessment = {
      ...assessmentData,
      results,
      shareCode,
      savedAt: new Date().toISOString(),
      type: 'guest_saved'
    };
    localStorage.setItem('savedGuestAssessment', JSON.stringify(savedAssessment));
    alert('Your assessment has been saved locally. You can return to this page to see your results or sign up to save them permanently.');
  };

  const copyShareLink = () => {
    const shareUrl = `${window.location.origin}/share/${shareCode}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      alert('Share link copied to clipboard!');
    });
  };

  if (!results) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <p className="text-gray-600">Loading your results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Guest Mode Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiUser} className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">
              Guest Assessment Results
            </span>
          </div>
          <span className="text-xs text-blue-600">
            Share Code: {shareCode}
          </span>
        </div>
      </div>

      {/* Overall Score */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-6">Your Assessment Results</h1>
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md mx-auto">
          <div className="flex items-center justify-center mb-4">
            <SafeIcon icon={results.overall.level.icon} className={`h-16 w-16 text-${results.overall.level.color}-500`} />
          </div>
          <div className="text-6xl font-bold text-gray-900 mb-2">
            {results.overall.percentage}%
          </div>
          <div className={`text-xl font-semibold text-${results.overall.level.color}-600 mb-2`}>
            {results.overall.level.level}
          </div>
          <p className="text-gray-600">Overall Team Health Score</p>
        </div>
      </div>

      {/* Save Options */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Save Your Results</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Create an Account</h3>
            <p className="text-gray-600 mb-4">
              Sign up to save your results, track progress over time, and access team management features.
            </p>
            <div className="space-y-3">
              <Link
                to="/register"
                className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={FiUserPlus} className="h-5 w-5" />
                <span>Sign Up to Save Results</span>
              </Link>
              <Link
                to="/login"
                className="flex items-center justify-center space-x-2 w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                <SafeIcon icon={FiLogIn} className="h-5 w-5" />
                <span>Sign In to Existing Account</span>
              </Link>
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Quick Options</h3>
            <p className="text-gray-600 mb-4">
              Save your results locally or share them with others.
            </p>
            <div className="space-y-3">
              <button
                onClick={handleSaveForLater}
                className="flex items-center justify-center space-x-2 w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                <SafeIcon icon={FiSave} className="h-5 w-5" />
                <span>Save for Later</span>
              </button>
              <button
                onClick={copyShareLink}
                className="flex items-center justify-center space-x-2 w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                <SafeIcon icon={FiUsers} className="h-5 w-5" />
                <span>Copy Share Link</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Section Scores */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        {results.sections.map((section, index) => (
          <div key={index} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{section.name}</h3>
              <SafeIcon icon={section.level.icon} className={`h-6 w-6 text-${section.level.color}-500`} />
            </div>
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>{section.level.level}</span>
                <span>{section.percentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`bg-${section.level.color}-500 h-2 rounded-full`} 
                  style={{ width: `${section.percentage}%` }}
                />
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Score: {section.score.toFixed(1)}/5.0
            </p>
          </div>
        ))}
      </div>

      {/* Detailed Recommendations */}
      <div className="space-y-8 mb-12">
        <h2 className="text-3xl font-bold text-gray-900 text-center">Personalized Recommendations</h2>
        {results.sections.map((section, index) => (
          <div key={index} className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{section.name}</h3>
              <div className={`px-4 py-2 rounded-full bg-${section.level.color}-100 text-${section.level.color}-800 font-semibold`}>
                {section.level.level}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Current Status</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-3xl font-bold text-gray-900 mb-2">{section.percentage}%</div>
                  <p className="text-gray-600">
                    Your team scored {section.score.toFixed(1)} out of 5.0 in this area.
                  </p>
                </div>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Action Items</h4>
                <ul className="space-y-2">
                  {getRecommendations(section.name, section.score).map((rec, recIndex) => (
                    <li key={recIndex} className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0" />
                      <span className="text-gray-700">{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={handleRetakeAssessment}
          className="flex items-center justify-center space-x-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
        >
          <SafeIcon icon={FiRefreshCw} className="h-5 w-5" />
          <span>Retake Assessment</span>
        </button>
        <Link
          to="/"
          className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
        >
          <span>Learn More</span>
        </Link>
      </div>
    </div>
  );
};

export default GuestResults;